import { supabase } from '../lib/supabase';
import { notifyAdmin } from '../lib/notify';

/**
 * DataService — Supabase edition.
 * Exposes the EXACT same method signatures as the original Firebase service
 * so pages (Admin, Recruiter, Colleges, Benefits, etc.) don't need changes.
 *
 * Table conventions:
 *   colleges.data (jsonb) — holds flexible college payload
 *   scholarships.data (jsonb)
 *   exams.data (jsonb)
 *   grant_applications — row per user, keyed by user_id
 *   partners — keyed by email
 *   leads — composite id `${userId}_${collegeId}`
 */

// Unwrap the `data` jsonb column back into top-level fields for callers.
// IMPORTANT: jsonb `data` is our source of truth (every CMS write goes there).
// Some legacy top-level columns (image_url, category, fees, ...) exist on the
// row as nulls — spreading `rest` AFTER `data` would let those nulls overwrite
// valid jsonb values. We spread `data` last so it always wins.
function unwrap(row) {
    if (!row) return null;
    const { data, ...rest } = row;
    return { ...rest, ...(data || {}), id: rest.id };
}

export const DataService = {
    /* ———— READ ———— */
    getAllColleges: async ({ includeHidden = false } = {}) => {
        try {
            const { data, error } = await supabase.from('colleges').select('*');
            if (error) throw error;
            const rows = (data || []).map(unwrap);
            // Hidden colleges live behind `data.hidden = true`. Filter on public reads;
            // admin pages pass { includeHidden: true } to see everything.
            const visible = includeHidden ? rows : rows.filter(r => r?.hidden !== true);
            // Sort by the admin-defined order (stored inside `data.sort_order`).
            // Rows without a sort_order sink to the bottom, sub-sorted by name
            // so legacy data still feels deterministic.
            return visible.sort((a, b) => {
                const ao = typeof a.sort_order === 'number' ? a.sort_order : Number.MAX_SAFE_INTEGER;
                const bo = typeof b.sort_order === 'number' ? b.sort_order : Number.MAX_SAFE_INTEGER;
                if (ao !== bo) return ao - bo;
                return (a.name || '').localeCompare(b.name || '');
            });
        } catch (e) {
            console.error('getAllColleges:', e?.message || e);
            return [];
        }
    },

    /**
     * Admin: toggle `data.hidden` on a single college row. Public reads (and
     * /colleges/:slug deep links) will skip hidden rows automatically.
     */
    setCollegeHidden: async (id, hidden) => {
        try {
            const { data: row, error: e1 } = await supabase
                .from('colleges')
                .select('data')
                .eq('id', id)
                .maybeSingle();
            if (e1) throw e1;
            const next = { ...(row?.data || {}), hidden: !!hidden };
            const { error: e2 } = await supabase
                .from('colleges')
                .update({ data: next })
                .eq('id', id);
            if (e2) throw e2;
            return { success: true };
        } catch (e) {
            console.error('setCollegeHidden:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    getCollegeById: async (id, { includeHidden = false } = {}) => {
        try {
            const { data, error } = await supabase.from('colleges').select('*').eq('id', id).maybeSingle();
            if (error) throw error;
            const row = unwrap(data);
            if (!row) return null;
            if (!includeHidden && row.hidden === true) return null;
            return row;
        } catch (e) {
            console.error('getCollegeById:', e?.message || e);
            return null;
        }
    },

    getAllScholarships: async () => {
        try {
            const { data, error } = await supabase.from('scholarships').select('*');
            if (error) throw error;
            return (data || []).map(unwrap);
        } catch (e) {
            console.error('getAllScholarships:', e?.message || e);
            return [];
        }
    },

    getAllGrantApplications: async () => {
        try {
            const { data, error } = await supabase.from('grant_applications').select('*');
            if (error) throw error;
            // Preserve legacy shape: { userId, name, email, eagerScore, status, appliedAt }
            return (data || []).map(r => ({
                userId: r.user_id,
                name: r.name,
                email: r.email,
                eagerScore: r.eager_score,
                status: r.status,
                appliedAt: r.applied_at,
                id: r.user_id,
            }));
        } catch (e) {
            console.error('getAllGrantApplications:', e?.message || e);
            return [];
        }
    },

    getAllUsers: async () => {
        try {
            const { data, error } = await supabase.from('users').select('*');
            if (error) throw error;
            // Map to camelCase for legacy consumers (Admin/Recruiter pages)
            return (data || []).map(r => ({
                id: r.id,
                uid: r.id,
                email: r.email,
                name: r.name,
                avatar: r.avatar,
                role: r.role,
                title: r.title,
                location: r.location,
                interests: r.interests || [],
                skills: r.skills || [],
                projects: r.projects || [],
                savedColleges: r.saved_colleges || [],
                applications: r.applications || [],
                academic: r.academic || {},
                quizResult: r.quiz_result,
                eagerScore: r.eager_score || 0,
                badges: r.badges || [],
                stats: r.stats || {},
                joinedAt: r.joined_at,
            }));
        } catch (e) {
            console.error('getAllUsers:', e?.message || e);
            return [];
        }
    },

    /* ———— ADMIN — Raw auth-level signup list (Social campaign tracking) ———— */
    /* Wraps the `admin_list_signups()` SECURITY DEFINER RPC. Returns one row
       per registered email (whether or not the user completed onboarding),
       latest first. Admin role enforced inside the RPC.
       Falls back to the legacy 9-column version if the new UTM-enabled RPC
       hasn't been deployed yet — keeps the UI from breaking pre-migration. */
    adminListSignups: async () => {
        try {
            const { data, error } = await supabase.rpc('admin_list_signups_v2');
            if (!error) return { success: true, rows: data || [], hasUtm: true };
            // v2 RPC missing — fall back to v1 (no UTM columns).
            const { data: legacy, error: legacyErr } = await supabase.rpc('admin_list_signups');
            if (legacyErr) throw legacyErr;
            return { success: true, rows: legacy || [], hasUtm: false };
        } catch (e) {
            return { success: false, error: e?.message || String(e), rows: [], hasUtm: false };
        }
    },

    /* ———— WRITE — USER ACTIONS ———— */
    applyToGrant: async (userId, userData) => {
        try {
            const { error } = await supabase.from('grant_applications').upsert({
                user_id: userId,
                name: userData.title || userData.displayName || userData.name || 'Unknown',
                email: userData.email,
                eager_score: userData.eagerScore || 0,
                status: 'pending',
                applied_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('applyToGrant:', error?.message);
            return { success: false, error };
        }
    },

    updateGrantStatus: async (userId, status) => {
        try {
            const { error } = await supabase.from('grant_applications').update({ status }).eq('user_id', userId);
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('updateGrantStatus:', error?.message);
            return { success: false, error };
        }
    },

    /* ———— GRANT CLAIMS (post-admission cashback) ———— */
    submitGrantClaim: async (payload) => {
        try {
            const { data, error } = await supabase.rpc('submit_grant_claim', {
                p_user_id:         payload.userId || null,
                p_full_name:       payload.fullName,
                p_email:           payload.email,
                p_phone:           payload.phone || null,
                p_college_name:    payload.collegeName,
                p_course_name:     payload.courseName || null,
                p_admission_date:  payload.admissionDate || null,
                p_fee_paid_amount: payload.feePaidAmount || null,
                p_proof_url:       payload.proofUrl || null,
                p_account_holder:  payload.accountHolder,
                p_bank_account:    payload.bankAccount,
                p_ifsc:            payload.ifsc,
                p_upi_id:          payload.upiId || null,
            });
            if (error) throw error;
            notifyAdmin('grant_claim', {
                full_name: payload.fullName, phone: payload.phone,
                college_name: payload.collegeName, email: payload.email,
            });
            return { success: true, id: data };
        } catch (error) {
            console.error('submitGrantClaim:', error?.message);
            return { success: false, error: error?.message || 'Could not submit claim' };
        }
    },

    uploadGrantClaimProof: async (file, namespace = 'anon') => {
        try {
            const ext = (file.name.split('.').pop() || 'pdf').toLowerCase();
            const safeNs = String(namespace).replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 32) || 'anon';
            const path = `${safeNs}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
            const { error } = await supabase.storage
                .from('grant-claim-proofs')
                .upload(path, file, { cacheControl: '3600', upsert: false });
            if (error) throw error;
            // The bucket is private — we store the path, admin reads via signed URL.
            return { success: true, path };
        } catch (error) {
            console.error('uploadGrantClaimProof:', error?.message);
            return { success: false, error: error?.message || 'Upload failed' };
        }
    },

    getAllGrantClaims: async () => {
        try {
            const { data, error } = await supabase.from('grant_claims')
                .select('*').order('created_at', { ascending: false });
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.error('getAllGrantClaims:', e?.message || e);
            return [];
        }
    },

    updateGrantClaim: async (id, patch) => {
        try {
            const { error } = await supabase.from('grant_claims').update(patch).eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('updateGrantClaim:', error?.message);
            return { success: false, error };
        }
    },

    deleteGrantClaim: async (id) => {
        try {
            const { error } = await supabase.from('grant_claims').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('deleteGrantClaim:', error?.message);
            return { success: false, error };
        }
    },

    getGrantClaimsForUser: async (userId) => {
        try {
            const { data, error } = await supabase.from('grant_claims')
                .select('*').eq('user_id', userId).order('created_at', { ascending: false });
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.error('getGrantClaimsForUser:', e?.message || e);
            return [];
        }
    },

    // Returns a short-lived (60s) signed URL for an admin to view a private proof file
    getSignedGrantProofUrl: async (path) => {
        try {
            const { data, error } = await supabase.storage
                .from('grant-claim-proofs').createSignedUrl(path, 60);
            if (error) throw error;
            return data?.signedUrl || null;
        } catch (e) {
            console.error('getSignedGrantProofUrl:', e?.message || e);
            return null;
        }
    },

    /* ———— UPI PAYMENTS (interim, pre-Razorpay) ———— */
    createPaymentRequest: async (payload) => {
        try {
            const { data, error } = await supabase.rpc('submit_payment_request', {
                p_user_id:      payload.userId || null,
                p_full_name:    payload.fullName,
                p_email:        payload.email,
                p_phone:        payload.phone || null,
                p_purpose:      payload.purpose || 'counseling',
                p_purpose_meta: payload.purposeMeta || {},
                p_amount_paise: payload.amountPaise,
            });
            if (error) throw error;
            // RPC returns jsonb { id, token }
            return { success: true, id: data?.id, token: data?.token };
        } catch (error) {
            console.error('createPaymentRequest:', error?.message);
            return { success: false, error: error?.message || 'Could not start payment' };
        }
    },

    uploadPaymentScreenshot: async (file, namespace = 'anon') => {
        try {
            const ext = (file.name.split('.').pop() || 'png').toLowerCase();
            const safeNs = String(namespace).replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 32) || 'anon';
            const path = `${safeNs}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
            const { error } = await supabase.storage
                .from('payment-screenshots')
                .upload(path, file, { cacheControl: '3600', upsert: false });
            if (error) throw error;
            return { success: true, path };
        } catch (error) {
            console.error('uploadPaymentScreenshot:', error?.message);
            return { success: false, error: error?.message || 'Upload failed' };
        }
    },

    attachPaymentProof: async ({ referenceToken, utr, screenshotPath }) => {
        try {
            const { data, error } = await supabase.rpc('attach_payment_proof', {
                p_reference_token: referenceToken,
                p_upi_utr:         utr,
                p_screenshot_path: screenshotPath || null,
            });
            if (error) throw error;
            return { success: true, ...data };
        } catch (error) {
            console.error('attachPaymentProof:', error?.message);
            return { success: false, error: error?.message || 'Could not attach proof' };
        }
    },

    getAllPaymentRequests: async () => {
        try {
            const { data, error } = await supabase.from('payment_requests')
                .select('*').order('created_at', { ascending: false });
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.error('getAllPaymentRequests:', e?.message || e);
            return [];
        }
    },

    updatePaymentRequest: async (id, patch) => {
        try {
            const { error } = await supabase.from('payment_requests').update(patch).eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('updatePaymentRequest:', error?.message);
            return { success: false, error };
        }
    },

    deletePaymentRequest: async (id) => {
        try {
            const { error } = await supabase.from('payment_requests').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('deletePaymentRequest:', error?.message);
            return { success: false, error };
        }
    },

    getSignedPaymentScreenshotUrl: async (path) => {
        try {
            const { data, error } = await supabase.storage
                .from('payment-screenshots').createSignedUrl(path, 60);
            if (error) throw error;
            return data?.signedUrl || null;
        } catch (e) {
            console.error('getSignedPaymentScreenshotUrl:', e?.message || e);
            return null;
        }
    },

    addPartner: async (partnerData) => {
        try {
            const { email, collegeName, authorized = true, ...rest } = partnerData;
            const { error } = await supabase.from('partners').upsert({
                email,
                college_name: collegeName,
                authorized,
                data: rest,
                created_at: new Date().toISOString(),
            }, { onConflict: 'email' });
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('addPartner:', error?.message);
            return { success: false, error };
        }
    },

    getAllPartners: async () => {
        try {
            const { data, error } = await supabase.from('partners').select('*');
            if (error) throw error;
            return (data || []).map(r => ({
                id: r.email,
                email: r.email,
                collegeName: r.college_name,
                authorized: r.authorized,
                ...(r.data || {}),
                createdAt: r.created_at,
            }));
        } catch (e) {
            console.error('getAllPartners:', e?.message);
            return [];
        }
    },

    submitCollegeLead: async (userId, collegeId, type = 'interest') => {
        try {
            const leadId = `${userId}_${collegeId}`;
            const { error } = await supabase.from('college_interest_leads').upsert({
                id: leadId,
                user_id: userId,
                college_id: collegeId,
                type,
                status: 'new',
                created_at: new Date().toISOString(),
            }, { onConflict: 'id' });
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('submitCollegeLead:', error?.message);
            return { success: false, error };
        }
    },

    /* ———— COUNSELING BOOKINGS ———— */
    submitCounselingBooking: async (booking) => {
        try {
            const { error } = await supabase.from('counseling_bookings').insert({
                user_id: booking.user_id || null,
                full_name: booking.full_name,
                email: booking.email,
                phone: booking.phone,
                preferred_date: booking.preferred_date || null,
                preferred_time: booking.preferred_time || null,
                topic: booking.topic,
                message: booking.message || null,
                payment_id: booking.payment_id || null,
                payment_status: booking.payment_status || 'pending',
                amount: booking.amount || null,
                status: 'new',
                created_at: new Date().toISOString(),
            });
            if (error) throw error;
            notifyAdmin('counseling_booking', {
                full_name: booking.full_name, email: booking.email, phone: booking.phone,
                topic: booking.topic, message: booking.message,
            });
            return { success: true };
        } catch (error) {
            console.error('submitCounselingBooking:', error?.message);
            return { success: false, error: error?.message || 'Booking failed' };
        }
    },

    /* ———— CMS ACTIONS ———— */
    addCollege: async (collegeData) => {
        try {
            const id = collegeData.id || collegeData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            // Stamp a sort_order if the caller didn't provide one — new colleges
            // append to the bottom of the existing list so drag-reorder is the
            // only way to change ordering.
            let sort_order = collegeData.sort_order;
            if (typeof sort_order !== 'number') {
                const { count } = await supabase.from('colleges').select('id', { count: 'exact', head: true });
                sort_order = typeof count === 'number' ? count : Date.now();
            }
            const { error } = await supabase.from('colleges').upsert({
                id,
                name: collegeData.name,
                data: { ...collegeData, id, slug: collegeData.slug || id, sort_order, updatedAt: new Date().toISOString() },
                created_at: new Date().toISOString(),
            }, { onConflict: 'id' });
            if (error) throw error;
            return { success: true, id };
        } catch (error) {
            console.error('addCollege:', error?.message);
            return { success: false, error };
        }
    },

    updateCollege: async (id, collegeData) => {
        try {
            // Preserve the existing sort_order unless the caller is explicitly setting it.
            let sort_order = collegeData.sort_order;
            if (typeof sort_order !== 'number') {
                const { data: prev } = await supabase.from('colleges').select('data').eq('id', id).maybeSingle();
                sort_order = prev?.data?.sort_order;
            }
            const { error } = await supabase.from('colleges').update({
                name: collegeData.name,
                data: { ...collegeData, id, sort_order, updatedAt: new Date().toISOString() },
            }).eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('updateCollege:', error?.message);
            return { success: false, error };
        }
    },

    deleteCollege: async (id) => {
        try {
            const { error } = await supabase.from('colleges').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('deleteCollege:', error?.message);
            return { success: false, error };
        }
    },

    /* ———— CAREER VIDEOS ———— */
    getAllCareerVideos: async () => {
        try {
            const { data, error } = await supabase
                .from('career_videos')
                .select('*')
                .eq('is_active', true)
                .order('sort_order', { ascending: true });
            if (error) throw error;
            return (data || []).map(r => ({
                id: r.id,
                title: r.title,
                archetype: r.archetype,
                courseId: r.course_id,
                courseTitle: r.course_title,
                youtubeId: r.youtube_id,
                videoUrl: r.video_url || null,
                creatorName: r.creator_name || null,
                creatorRole: r.creator_role || null,
                duration: r.duration,
                thumbnail: r.thumbnail,
                tasks: r.tasks || [],
                sort_order: r.sort_order,
            }));
        } catch (e) {
            console.error('getAllCareerVideos:', e?.message);
            return [];
        }
    },

    upsertCareerVideo: async (video) => {
        try {
            const id = video.id || `vid-${Date.now()}`;
            const row = {
                id,
                title: video.title,
                archetype: video.archetype,
                course_id: video.courseId || null,
                course_title: video.courseTitle || null,
                youtube_id: video.youtubeId,
                duration: video.duration || null,
                thumbnail: video.thumbnail || null,
                tasks: video.tasks || [],
                sort_order: typeof video.sort_order === 'number' ? video.sort_order : 0,
                is_active: video.is_active !== false,
                updated_at: new Date().toISOString(),
                video_url: video.videoUrl || null,
                creator_name: video.creatorName || null,
                creator_role: video.creatorRole || null,
            };
            let { error } = await supabase.from('career_videos').upsert(row, { onConflict: 'id' });
            if (error && /column|schema cache/i.test(error.message || '')) {
                // Prod DB not yet migrated with video-bytes-setup.sql — save the base fields.
                const { video_url, creator_name, creator_role, ...base } = row;
                ({ error } = await supabase.from('career_videos').upsert(base, { onConflict: 'id' }));
            }
            if (error) throw error;
            return { success: true, id };
        } catch (error) {
            console.error('upsertCareerVideo:', error?.message);
            return { success: false, error };
        }
    },

    deleteCareerVideo: async (id) => {
        try {
            const { error } = await supabase.from('career_videos').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('deleteCareerVideo:', error?.message);
            return { success: false, error };
        }
    },

    reorderCareerVideos: async (orderedIds = []) => {
        try {
            await Promise.all(orderedIds.map((id, idx) =>
                supabase.from('career_videos').update({ sort_order: idx }).eq('id', id)
            ));
            return { success: true };
        } catch (error) {
            console.error('reorderCareerVideos:', error?.message);
            return { success: false, error };
        }
    },

    /* ———— PHASE-1 COURSES ———— */
    getAllPhase1Courses: async ({ includeHidden = false } = {}) => {
        try {
            let q = supabase
                .from('phase1_courses')
                .select('*')
                .order('sort_order', { ascending: true });
            if (!includeHidden) q = q.eq('is_active', true);
            const { data, error } = await q;
            if (error) throw error;
            return (data || []).map(r => ({
                id: r.id,
                title: r.title,
                type: r.type,
                desc: r.description,
                duration: r.duration,
                exams: r.exams || [],
                imageId: r.image_id,
                sort_order: r.sort_order,
                is_active: r.is_active !== false,
            }));
        } catch (e) {
            console.error('getAllPhase1Courses:', e?.message);
            return [];
        }
    },

    /**
     * Admin: hide / un-hide a Phase-1 course. Public Explore page reads
     * `is_active = true` only, so hidden courses disappear from the catalog
     * but stay editable inside the admin.
     */
    setPhase1CourseHidden: async (id, hidden) => {
        try {
            const { error } = await supabase
                .from('phase1_courses')
                .update({ is_active: !hidden, updated_at: new Date().toISOString() })
                .eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) {
            console.error('setPhase1CourseHidden:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    upsertPhase1Course: async (course) => {
        try {
            const id = course.id || course.title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
            const { error } = await supabase.from('phase1_courses').upsert({
                id,
                title: course.title,
                type: course.type || 'Design',
                description: course.desc || course.description || null,
                duration: course.duration || null,
                exams: course.exams || [],
                image_id: course.imageId || null,
                sort_order: typeof course.sort_order === 'number' ? course.sort_order : 0,
                is_active: course.is_active !== false,
                updated_at: new Date().toISOString(),
            }, { onConflict: 'id' });
            if (error) throw error;
            return { success: true, id };
        } catch (error) {
            console.error('upsertPhase1Course:', error?.message);
            return { success: false, error };
        }
    },

    deletePhase1Course: async (id) => {
        try {
            const { error } = await supabase.from('phase1_courses').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('deletePhase1Course:', error?.message);
            return { success: false, error };
        }
    },

    /* Bulk-persist the admin's drag-reordered Phase-1 courses. */
    reorderPhase1Courses: async (orderedIds = []) => {
        try {
            await Promise.all(orderedIds.map((id, idx) =>
                supabase.from('phase1_courses').update({ sort_order: idx }).eq('id', id)
            ));
            return { success: true };
        } catch (error) {
            console.error('reorderPhase1Courses:', error?.message);
            return { success: false, error };
        }
    },

    /* Bulk-persist the admin's drag-reordered colleges.
       sort_order lives inside the `data` jsonb blob, so we have to re-fetch
       each row, merge, and write back. We do it sequentially-batched to keep
       the round-trips small. */
    reorderColleges: async (orderedIds = []) => {
        try {
            // Pull just the data blobs we need so we can merge without clobbering other fields.
            const { data: existing, error: readErr } = await supabase
                .from('colleges').select('id, data').in('id', orderedIds);
            if (readErr) throw readErr;
            const byId = Object.fromEntries((existing || []).map(r => [r.id, r.data || {}]));
            await Promise.all(orderedIds.map((id, idx) =>
                supabase.from('colleges')
                    .update({ data: { ...(byId[id] || {}), id, sort_order: idx } })
                    .eq('id', id)
            ));
            return { success: true };
        } catch (error) {
            console.error('reorderColleges:', error?.message);
            return { success: false, error };
        }
    },

    /* ———— COURSE → COLLEGE RECOMMENDATIONS ———— */
    getAllCourseRecommendations: async () => {
        try {
            const { data, error } = await supabase.from('course_recommendations').select('*');
            if (error) throw error;
            const map = {};
            (data || []).forEach(r => { map[r.course_id] = r.college_ids || []; });
            return map;
        } catch (e) {
            console.error('getAllCourseRecommendations:', e?.message);
            return {};
        }
    },

    setCourseRecommendation: async (courseId, collegeIds) => {
        try {
            const { error } = await supabase.from('course_recommendations').upsert({
                course_id: courseId,
                college_ids: collegeIds || [],
                updated_at: new Date().toISOString(),
            }, { onConflict: 'course_id' });
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('setCourseRecommendation:', error?.message);
            return { success: false, error };
        }
    },

    /* ———— CONTACT MESSAGES (public form → admin inbox) ———— */
    submitContactMessage: async ({ name, email, message }) => {
        try {
            // Public submit goes through a SECURITY DEFINER RPC function so anon
            // users can insert without needing direct table-level INSERT privilege.
            const { data, error } = await supabase.rpc('submit_contact_message', {
                p_name: name,
                p_email: email,
                p_message: message,
                p_user_agent: typeof navigator !== 'undefined'
                    ? navigator.userAgent.slice(0, 500)
                    : null,
            });
            if (error) throw error;
            notifyAdmin('contact_message', { name, email, message });
            return { success: true, id: data };
        } catch (error) {
            console.error('submitContactMessage:', error?.message || error);
            return { success: false, error: error?.message || 'Could not submit message' };
        }
    },

    getAllContactMessages: async () => {
        try {
            const { data, error } = await supabase
                .from('contact_messages')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.error('getAllContactMessages:', e?.message);
            return [];
        }
    },

    markContactMessageRead: async (id, isRead = true) => {
        try {
            const { error } = await supabase.from('contact_messages')
                .update({ is_read: isRead }).eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) { return { success: false, error: e?.message }; }
    },

    deleteContactMessage: async (id) => {
        try {
            const { error } = await supabase.from('contact_messages').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) { return { success: false, error: e?.message }; }
    },

    /* ———— EAGER MINDS SUBMISSIONS ———— */
    submitEagerMind: async ({ name, email, city, field, portfolio, tagline, whatsapp }) => {
        try {
            const { data, error } = await supabase.rpc('submit_eager_mind', {
                p_name: name,
                p_email: email,
                p_city: city || null,
                p_field: field || null,
                p_portfolio: portfolio || null,
                p_tagline: tagline || null,
                p_whatsapp: whatsapp || null,
                p_user_agent: typeof navigator !== 'undefined'
                    ? navigator.userAgent.slice(0, 500) : null,
            });
            if (error) throw error;
            return { success: true, id: data };
        } catch (error) {
            console.error('submitEagerMind:', error?.message || error);
            return { success: false, error: error?.message || 'Could not submit' };
        }
    },

    getAllEagerMindsSubmissions: async () => {
        try {
            const { data, error } = await supabase
                .from('eager_minds_submissions')
                .select('*').order('created_at', { ascending: false });
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.error('getAllEagerMindsSubmissions:', e?.message);
            return [];
        }
    },

    updateEagerMindStatus: async (id, status) => {
        try {
            const { error } = await supabase.from('eager_minds_submissions')
                .update({ status, is_read: true, reviewed_at: new Date().toISOString() }).eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) { return { success: false, error: e?.message }; }
    },

    /**
     * Full moderation patch — admin can update status, badge, reflection
     * notes, and featured flag in a single call.
     */
    updateEagerMindModeration: async (id, patch) => {
        try {
            const update = { reviewed_at: new Date().toISOString(), is_read: true };
            if (patch.status !== undefined)            update.status            = patch.status;
            if (patch.assigned_badge !== undefined)    update.assigned_badge    = patch.assigned_badge;
            if (patch.reflection_notes !== undefined)  update.reflection_notes  = patch.reflection_notes;
            if (patch.is_featured !== undefined)       update.is_featured       = patch.is_featured;
            const { data, error } = await supabase.from('eager_minds_submissions')
                .update(update).eq('id', id).select().single();
            if (error) throw error;
            return { success: true, row: data };
        } catch (e) {
            console.error('updateEagerMindModeration:', e?.message);
            return { success: false, error: e?.message };
        }
    },

    deleteEagerMindSubmission: async (id) => {
        try {
            const { error } = await supabase.from('eager_minds_submissions').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) { return { success: false, error: e?.message }; }
    },

    /**
     * Submit Your Work v2 — richer payload + optional file upload.
     *  1. RPC `submit_eager_mind_v2`     → inserts row, returns id
     *  2. If a file is provided, uploads to Storage bucket `eager-minds-work`
     *     using the row id as the path key. Upload progress is reported
     *     via the `onProgress(0..1)` callback.
     *  3. RPC `attach_eager_mind_file`   → patches the row with the public URL
     */
    submitEagerMindV2: async (payload, file, onProgress) => {
        try {
            // — 1. Insert the row —
            const { data: idData, error: rpcErr } = await supabase.rpc('submit_eager_mind_v2', {
                p_name:           payload.name,
                p_email:          payload.email,
                p_whatsapp:       payload.whatsapp,
                p_age:            payload.age,
                p_city:           payload.city || null,
                p_field:          payload.field,
                p_title:          payload.title,
                p_why_made:       payload.whyMade,
                p_portfolio:      payload.portfolio || null,
                p_tagline:        payload.tagline   || null,
                p_ai_assisted:    !!payload.aiAssisted,
                p_parent_consent: !!payload.parentConsent,
                p_user_agent:     typeof navigator !== 'undefined'
                    ? navigator.userAgent.slice(0, 500) : null,
            });
            if (rpcErr) throw rpcErr;
            const id = idData;

            // — 2. Upload the file (if provided) —
            if (file) {
                // Resolve file kind. Server-side bucket policy enforces MIME.
                const mime = file.type || '';
                let kind = 'image';
                if (mime === 'application/pdf') kind = 'pdf';
                else if (mime.startsWith('video/')) kind = 'video';

                const ext  = (file.name.split('.').pop() || 'bin').toLowerCase();
                const path = `${id}.${ext}`;

                onProgress?.(0.05);
                const { error: upErr } = await supabase.storage
                    .from('eager-minds-work')
                    .upload(path, file, {
                        cacheControl: '3600',
                        upsert: false,
                        contentType: mime || undefined,
                    });
                if (upErr) throw upErr;
                onProgress?.(0.85);

                const { data: pub } = supabase.storage
                    .from('eager-minds-work').getPublicUrl(path);
                const fileUrl = pub?.publicUrl;

                // — 3. Patch the row with the URL —
                const { error: attachErr } = await supabase.rpc('attach_eager_mind_file', {
                    p_id:        id,
                    p_file_url:  fileUrl,
                    p_file_type: kind,
                    p_file_size: file.size,
                });
                if (attachErr) throw attachErr;
                onProgress?.(1);
            } else {
                onProgress?.(1);
            }

            notifyAdmin('eager_minds', {
                name: payload.name, email: payload.email, title: payload.title,
            });
            return { success: true, id };
        } catch (error) {
            console.error('submitEagerMindV2:', error?.message || error);
            return { success: false, error: error?.message || 'Could not submit your work right now. Please try again.' };
        }
    },

    /**
     * Fetch approved/featured submissions for the public Featured Minds grid.
     * No PII — only fields surfaced by the get_featured_minds_public RPC.
     */
    getFeaturedMindsPublic: async () => {
        try {
            const { data, error } = await supabase.rpc('get_featured_minds_public');
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.warn('getFeaturedMindsPublic (returning []):', e?.message);
            return [];
        }
    },


    /* ———— STORAGE: USER AVATARS ———— */
    uploadUserAvatar: async (file, userId) => {
        try {
            if (!userId) throw new Error('userId required for avatar upload');
            const path = `${userId}/avatar.jpg`; // userId folder so RLS policy can match
            const { error: upErr } = await supabase.storage
                .from('user-avatars')
                .upload(path, file, {
                    upsert: true,
                    cacheControl: '60',
                    contentType: 'image/jpeg',
                });
            if (upErr) throw upErr;
            const { data: urlData } = supabase.storage.from('user-avatars').getPublicUrl(path);
            // Cache-bust so the new image shows up immediately even though the path is stable
            const url = `${urlData.publicUrl}?v=${Date.now()}`;
            return { success: true, url, path };
        } catch (error) {
            console.error('uploadUserAvatar:', error?.message || error);
            return { success: false, error: error?.message || 'Upload failed' };
        }
    },

    /* ———— STORAGE: COLLEGE IMAGES ———— */
    uploadCollegeImage: async (file, collegeId) => {
        try {
            const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
            const path = `${collegeId}.${ext}`;
            // Upload (overwrite if exists). Short cache so admin edits show up fast.
            const { error: uploadErr } = await supabase.storage
                .from('college-images')
                .upload(path, file, { upsert: true, cacheControl: '60', contentType: file.type || `image/${ext}` });
            if (uploadErr) throw uploadErr;
            // Public URL with cache-buster so the browser / Supabase CDN doesn't
            // return a stale copy when the SAME path is re-uploaded.
            const { data: urlData } = supabase.storage.from('college-images').getPublicUrl(path);
            const url = `${urlData.publicUrl}?v=${Date.now()}`;
            return { success: true, url, path };
        } catch (error) {
            console.error('uploadCollegeImage:', error?.message || error);
            return { success: false, error: error?.message || 'Upload failed' };
        }
    },

    /* ———— SEEDING ———— */
    seedDatabase: async (collegesData, scholarshipsData, examsData) => {
        console.log('Starting Supabase Seeding...');
        for (const c of collegesData || []) {
            const id = c.id || c.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
            await supabase.from('colleges').upsert({ id, name: c.name, data: { ...c, id } }, { onConflict: 'id' });
        }
        for (const s of scholarshipsData || []) {
            const id = s.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
            await supabase.from('scholarships').upsert({ id, name: s.name, data: s }, { onConflict: 'id' });
        }
        for (const e of examsData || []) {
            const id = e.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
            await supabase.from('exams').upsert({ id, name: e.name, data: e }, { onConflict: 'id' });
        }
        console.log('Supabase Seeding Completed');
    },

    /**
     * One-shot seed for Phase-1 launch:
     * Pushes the verified static datasets (colleges, videos, courses, recommendations)
     * into Supabase. Idempotent — safe to run multiple times.
     */
    seedPhase1Verified: async ({ colleges = [], careerVideos = [], phase1Courses = [], courseRecommendations = {} }) => {
        const now = new Date().toISOString();
        let counts = { colleges: 0, videos: 0, courses: 0, recommendations: 0 };
        let errors = [];

        // 1. Colleges — pre-fetch existing image_urls so re-seeding never wipes
        // out a thumbnail uploaded via the CMS / Bulk Image Import.
        const existingImageUrls = {};
        try {
            const { data: existingRows } = await supabase.from('colleges').select('id,data');
            (existingRows || []).forEach(r => {
                const url = (r.data || {}).image_url;
                if (url) existingImageUrls[r.id] = url;
            });
        } catch (e) {
            console.warn('preserve image_url lookup failed:', e?.message);
        }

        for (const c of colleges) {
            const id = c.id || c.slug || c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            const preservedImageUrl = existingImageUrls[id];
            const dataPayload = {
                ...c,
                id,
                slug: c.slug || id,
                ...(preservedImageUrl ? { image_url: preservedImageUrl } : {}),
            };
            const { error } = await supabase.from('colleges').upsert({
                id,
                name: c.name,
                data: dataPayload,
                created_at: now,
            }, { onConflict: 'id' });
            if (!error) counts.colleges++;
            else { console.warn(`college ${id}:`, error.message); errors.push({ table: 'colleges', id, msg: error.message }); }
        }

        // 2. Career videos
        for (let i = 0; i < careerVideos.length; i++) {
            const v = careerVideos[i];
            const { error } = await supabase.from('career_videos').upsert({
                id: v.id,
                title: v.title,
                archetype: v.archetype,
                course_id: v.courseId || null,
                course_title: v.courseTitle || null,
                youtube_id: v.youtubeId,
                duration: v.duration || null,
                thumbnail: v.thumbnail || null,
                tasks: v.tasks || [],
                sort_order: i,
                is_active: true,
                updated_at: now,
            }, { onConflict: 'id' });
            if (!error) counts.videos++;
            else { console.warn(`video ${v.id}:`, error.message); errors.push({ table: 'career_videos', id: v.id, msg: error.message }); }
        }

        // 3. Phase-1 courses
        for (let i = 0; i < phase1Courses.length; i++) {
            const c = phase1Courses[i];
            const id = c.id || c.title.toLowerCase().replace(/[^a-z0-9]+/g, '_');
            const { error } = await supabase.from('phase1_courses').upsert({
                id,
                title: c.title,
                type: c.type || 'Design',
                description: c.desc || c.description || null,
                duration: c.duration || null,
                exams: c.exams || [],
                image_id: c.imageId || id,
                sort_order: i,
                is_active: true,
                updated_at: now,
            }, { onConflict: 'id' });
            if (!error) counts.courses++;
            else { console.warn(`course ${id}:`, error.message); errors.push({ table: 'phase1_courses', id, msg: error.message }); }
        }

        // 4. Recommendations (course_id -> [college_ids])
        for (const [courseId, collegeIds] of Object.entries(courseRecommendations)) {
            const { error } = await supabase.from('course_recommendations').upsert({
                course_id: courseId,
                college_ids: collegeIds || [],
                updated_at: now,
            }, { onConflict: 'course_id' });
            if (!error) counts.recommendations++;
            else { console.warn(`rec ${courseId}:`, error.message); errors.push({ table: 'course_recommendations', id: courseId, msg: error.message }); }
        }

        return { success: errors.length === 0, counts, errors };
    },

    /* ———— CAREER PAGES (vertical landing pages: /gaming, /fashion, etc.) ———— */
    /**
     * Returns all rows from `career_pages`, sorted by sort_order.
     * Public read — anonymous traffic relies on this on /careers/:slug.
     * Returns [] on any failure (UI falls back to static defaults).
     */
    getAllCareerPages: async () => {
        try {
            const { data, error } = await supabase
                .from('career_pages')
                .select('*')
                .order('sort_order', { ascending: true });
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.warn('getAllCareerPages:', e?.message || e);
            return [];
        }
    },

    /**
     * Fetch a single career page by slug. Returns null if missing.
     * UI MUST treat null as "fall back to static default" rather than 404.
     */
    getCareerPageBySlug: async (slug) => {
        try {
            const { data, error } = await supabase
                .from('career_pages')
                .select('*')
                .eq('slug', slug)
                .maybeSingle();
            if (error) throw error;
            return data;
        } catch (e) {
            console.warn('getCareerPageBySlug:', e?.message || e);
            return null;
        }
    },

    /**
     * Admin upsert. Pass the full row shape (slug is the primary key).
     */
    upsertCareerPage: async (row) => {
        try {
            const payload = { ...row, updated_at: new Date().toISOString() };
            const { data, error } = await supabase
                .from('career_pages')
                .upsert(payload, { onConflict: 'slug' })
                .select()
                .maybeSingle();
            if (error) throw error;
            return { success: true, data };
        } catch (e) {
            console.error('upsertCareerPage:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    /* ———— LEADS (ad-traffic captured via landing pages) ———————————————— */
    /**
     * Public submit — anyone can drop a lead from a landing page form.
     * RLS allows INSERT without auth. Captures full UTM stamp so we can
     * later prove to partner colleges which signups Eager drove.
     */
    submitLead: async (lead) => {
        const payload = {
            name: (lead.name || '').trim(),
            phone: (lead.phone || '').trim(),
            email: (lead.email || '').trim() || null,
            course_interest: lead.course_interest || null,
            qualification: lead.qualification || null,
            source: lead.source || null,
            utm_source: lead.utm_source || null,
            utm_medium: lead.utm_medium || null,
            utm_campaign: lead.utm_campaign || null,
            utm_content: lead.utm_content || null,
            utm_term: lead.utm_term || null,
            utm_referrer: lead.utm_referrer || null,
            utm_landing_page: lead.utm_landing_page || null,
            status: 'new',
        };
        if (!payload.name || !payload.phone) {
            return { success: false, error: 'Name and phone are required.' };
        }
        // CRITICAL: notify admin FIRST so a real lead is never lost even if
        // Supabase RLS blocks the insert (as in the Webrocz Google Ads incident
        // Feb 2026). Saleem's phone is now the guaranteed capture channel.
        notifyAdmin('lead', {
            name: payload.name, phone: payload.phone, email: payload.email,
            course_interest: payload.course_interest, qualification: payload.qualification,
        });
        try {
            const { error } = await supabase
                .from('leads')
                .insert(payload);
            if (error) throw error;
            return { success: true, data: null };
        } catch (e) {
            const msg = e?.message || String(e);
            console.error('submitLead:', msg);
            // If the DB write failed but we already fired the WhatsApp, the
            // user is not lost — Saleem has the lead. Tell the UI it's OK so
            // the student sees the success screen and doesn't refresh + spam
            // the form. Log the error for our monitoring though.
            if (msg && /row-level security|violates|permission denied/i.test(msg)) {
                try { console.warn('[submitLead] RLS blocked insert — WhatsApp fallback used'); } catch (_e) { /* noop */ }
                return { success: true, data: null, fallbackOnly: true };
            }
            return { success: false, error: msg };
        }
    },

    /** Admin: list ALL leads, newest first. */
    getAllLeads: async () => {
        try {
            const { data, error } = await supabase
                .from('leads')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.warn('getAllLeads:', e?.message || e);
            return [];
        }
    },

    /** Admin: update lead status / notes. */
    updateLead: async (id, patch) => {
        try {
            const { data, error } = await supabase
                .from('leads')
                .update({ ...patch, updated_at: new Date().toISOString() })
                .eq('id', id)
                .select()
                .maybeSingle();
            if (error) throw error;
            return { success: true, data };
        } catch (e) {
            console.error('updateLead:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    /** Admin: delete a lead (rare, but handy for spam). */
    deleteLead: async (id) => {
        try {
            const { error } = await supabase.from('leads').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) {
            console.error('deleteLead:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    /* ———— PARTNER COLLEGES (Hamstech, NIFT, etc. for /apply/:slug) ————— */
    getAllPartnerColleges: async ({ includeHidden = false } = {}) => {
        try {
            let q = supabase.from('partner_colleges').select('*').order('sort_order', { ascending: true });
            if (!includeHidden) q = q.eq('visible', true);
            const { data, error } = await q;
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.warn('getAllPartnerColleges:', e?.message || e);
            return [];
        }
    },

    getPartnerCollegeBySlug: async (slug) => {
        try {
            const { data, error } = await supabase
                .from('partner_colleges')
                .select('*')
                .eq('slug', slug)
                .eq('visible', true)
                .maybeSingle();
            if (error) throw error;
            return data;
        } catch (e) {
            console.warn('getPartnerCollegeBySlug:', e?.message || e);
            return null;
        }
    },

    /** Admin: upsert a partner college row. */
    upsertPartnerCollege: async (row) => {
        try {
            const payload = { ...row, updated_at: new Date().toISOString() };
            const { data, error } = await supabase
                .from('partner_colleges')
                .upsert(payload, { onConflict: 'slug' })
                .select()
                .maybeSingle();
            if (error) throw error;
            return { success: true, data };
        } catch (e) {
            console.error('upsertPartnerCollege:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    deletePartnerCollege: async (slug) => {
        try {
            const { error } = await supabase.from('partner_colleges').delete().eq('slug', slug);
            if (error) throw error;
            return { success: true };
        } catch (e) {
            console.error('deletePartnerCollege:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    /* ———— LEAD REFERRALS (proof for partner-college invoicing) ————————— */
    /** Admin: refer a lead to a partner college. Returns the handoff URL to share. */
    referLeadToPartner: async (leadId, partnerSlug, adminId = null, notes = null) => {
        try {
            const { data, error } = await supabase
                .from('lead_referrals')
                .insert({
                    lead_id: leadId,
                    partner_slug: partnerSlug,
                    referred_by: adminId,
                    notes,
                })
                .select()
                .maybeSingle();
            if (error) throw error;
            // also flip lead.status to 'referred'
            await supabase
                .from('leads')
                .update({ status: 'referred', updated_at: new Date().toISOString() })
                .eq('id', leadId);
            return { success: true, data };
        } catch (e) {
            console.error('referLeadToPartner:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    /** Admin: list referrals (optionally filtered by partner_slug for invoicing). */
    getAllLeadReferrals: async ({ partnerSlug = null } = {}) => {
        try {
            let q = supabase
                .from('lead_referrals')
                .select('*, leads ( name, phone, email, course_interest, created_at )')
                .order('referred_at', { ascending: false });
            if (partnerSlug) q = q.eq('partner_slug', partnerSlug);
            const { data, error } = await q;
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.warn('getAllLeadReferrals:', e?.message || e);
            return [];
        }
    },

    /**
     * Public: log when a referred lead actually clicked through to the partner.
     * Called from /apply/:slug handoff page just before redirect.
     * RLS allows anyone to set `clicked_at` (we don't trust the lead ID).
     */
    logReferralClick: async (referralId) => {
        try {
            const { error } = await supabase
                .from('lead_referrals')
                .update({ clicked_at: new Date().toISOString() })
                .eq('id', referralId)
                .is('clicked_at', null);
            if (error) throw error;
            return { success: true };
        } catch (e) {
            console.warn('logReferralClick:', e?.message || e);
            return { success: false };
        }
    },

    /* ———— PARTNERSHIP REQUESTS (/claim-profile form) ————————————————————
     * Colleges & institutions ping us via /claim-profile. Historically the
     * form used a fake setTimeout stub — every submission was silently
     * dropped. Now every submission lands in `partnership_requests` and
     * shows up in /admin -> Partnership Requests.
     */
    submitPartnershipRequest: async (row) => {
        try {
            const payload = {
                college_name: (row.college_name || '').trim(),
                official_email: (row.official_email || '').trim().toLowerCase(),
                contact_name: (row.contact_name || '').trim(),
                phone: (row.phone || '').trim(),
                role: row.role || null,
                notes: row.notes || null,
                utm_source: row.utm_source || null,
                utm_medium: row.utm_medium || null,
                utm_campaign: row.utm_campaign || null,
                utm_content: row.utm_content || null,
                utm_landing_page: row.utm_landing_page || null,
                status: 'new',
            };
            if (!payload.college_name || !payload.official_email || !payload.contact_name || !payload.phone) {
                return { success: false, error: 'All required fields must be filled.' };
            }
            // IMPORTANT: no `.select()` chain here.
            // supabase-js chains `.insert().select()` cause PostgREST to add
            // `RETURNING *`, which requires SELECT privilege on top of INSERT.
            // Our RLS/GRANT setup gives anon INSERT-only (SELECT is admin-only
            // by design — partnership submissions must not be readable back
            // to any anon user). So we do a plain insert and only surface
            // success/error to the caller — none of the callers use `.data`.
            const { error } = await supabase
                .from('partnership_requests')
                .insert(payload);
            if (error) throw error;
            // Fire admin WhatsApp — closes the "Annapurna" silence gap.
            notifyAdmin('partnership_request', {
                college_name: payload.college_name,
                official_email: payload.official_email,
                contact_name: payload.contact_name,
                phone: payload.phone,
                role: payload.role,
            });
            return { success: true };
        } catch (e) {
            console.error('submitPartnershipRequest:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    getAllPartnershipRequests: async () => {
        try {
            const { data, error } = await supabase
                .from('partnership_requests')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.warn('getAllPartnershipRequests:', e?.message || e);
            return [];
        }
    },

    updatePartnershipRequest: async (id, patch) => {
        try {
            const { error } = await supabase
                .from('partnership_requests')
                .update({ ...patch, updated_at: new Date().toISOString() })
                .eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) {
            console.error('updatePartnershipRequest:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    deletePartnershipRequest: async (id) => {
        try {
            const { error } = await supabase.from('partnership_requests').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) {
            console.error('deletePartnershipRequest:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    /* ———— NOTIFY SIGNUPS ("Notify me when X launches" email capture) ——
     * Used by:
     *   - UpcomingStreams cards on Home/Explore (source='upcoming_stream')
     *   - RoadmapModal on Compass results (source='roadmap')
     * Previously fired only a PostHog event — emails were lost forever
     * unless you dug into PostHog Events dashboard.
     */
    submitNotifySignup: async (row) => {
        try {
            const payload = {
                email: (row.email || '').trim().toLowerCase(),
                source: row.source || 'other',
                vertical: row.vertical || null,
                user_id: row.user_id || null,
                notes: row.notes || null,
                utm_source: row.utm_source || null,
                utm_medium: row.utm_medium || null,
                utm_campaign: row.utm_campaign || null,
                utm_content: row.utm_content || null,
                utm_landing_page: row.utm_landing_page || null,
            };
            if (!payload.email) {
                return { success: false, error: 'Email is required.' };
            }
            // No `.select()` chain — see submitPartnershipRequest for the
            // full rationale. anon must NOT be able to read notify_signups
            // (leaks other users' emails), so we grant INSERT-only and
            // skip RETURNING here.
            const { error } = await supabase
                .from('notify_signups')
                .insert(payload);
            if (error) throw error;
            notifyAdmin('notify_signup', {
                email: payload.email, source: payload.source, vertical: payload.vertical,
            });
            return { success: true };
        } catch (e) {
            console.error('submitNotifySignup:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    getAllNotifySignups: async () => {
        try {
            const { data, error } = await supabase
                .from('notify_signups')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.warn('getAllNotifySignups:', e?.message || e);
            return [];
        }
    },

    markNotifySignupContacted: async (id, contacted = true) => {
        try {
            const { error } = await supabase
                .from('notify_signups')
                .update({ contacted })
                .eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) { return { success: false, error: e?.message }; }
    },

    deleteNotifySignup: async (id) => {
        try {
            const { error } = await supabase.from('notify_signups').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) {
            console.error('deleteNotifySignup:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    /* ———— COUNSELING BOOKINGS (admin surface for /counseling + /admission) ——
     * These have been saving to `counseling_bookings` all along — we just
     * never had an admin UI, so 3 days can pass without anyone noticing.
     */
    getAllCounselingBookings: async () => {
        try {
            const { data, error } = await supabase
                .from('counseling_bookings')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.warn('getAllCounselingBookings:', e?.message || e);
            return [];
        }
    },

    updateCounselingBooking: async (id, patch) => {
        try {
            const { error } = await supabase
                .from('counseling_bookings')
                .update(patch)
                .eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) {
            console.error('updateCounselingBooking:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    deleteCounselingBooking: async (id) => {
        try {
            const { error } = await supabase.from('counseling_bookings').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) {
            console.error('deleteCounselingBooking:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },

    /* ———— COLLEGE INTEREST LEADS (legacy /colleges "I'm interested" button) —
     * Saved to `college_interest_leads` — the LEGACY table. No admin UI
     * existed until now. Read-only + delete for admin.
     */
    getAllCollegeInterestLeads: async () => {
        try {
            const { data, error } = await supabase
                .from('college_interest_leads')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.warn('getAllCollegeInterestLeads:', e?.message || e);
            return [];
        }
    },

    deleteCollegeInterestLead: async (id) => {
        try {
            const { error } = await supabase.from('college_interest_leads').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (e) {
            console.error('deleteCollegeInterestLead:', e?.message || e);
            return { success: false, error: e?.message || String(e) };
        }
    },
};
